<template>
    <div
        :class="[{ 'modal fade flex items-center p-2 md:p-0': true, show: showModal, close: !showModal }]"
        id="quiz-config-setting-modal"
    >
        <div @click="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
        <div class="modal-content w-full h-full flex flex-col relative">
            <InnerLoader v-if="isLoading" :z-index="999" />
            <div class="modal-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">
                    {{
                        $t('setting_modal_header_quiz_settings', {
                            type: type.charAt(0).toUpperCase() + type.slice(1)
                        })
                    }}
                </h3>
                <i @click="closeModal()" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
            </div>
            <div class="modal-body w-full h-full p-4 pb-16 overflow-y-auto font-size-16">
                <QuizCreatorImage
                    @remove="
                        () => {
                            removeQuizImage()
                            checkedToValidate()
                        }
                    "
                    :class="!canEditQuiz ? 'pointer-events-none' : ''"
                    :already-show="true"
                    :image="image"
                    :image-original="imageOriginal"
                    :image-source="imageSource"
                    :image-coordinates="imageCoordinates"
                    :title="$t('setting_upload_image')"
                    :placeholder="`${$t('setting_upload')}<br>
                        ${$t('setting_upload_image')}<br>
                        ${$t('setting_upload_optional')}`"
                    ref="quizImage"
                    id="quiz-image"
                    color="#ccc"
                    border-color="#ccc"
                    border-style="dashed"
                    location="quiz"
                />
                <div class="shadow-text-none">
                    <label for="quiz-title" class="font-bold">{{ $t('setting_header_title') }}</label>
                    <XInput
                        v-validate="'required'"
                        @input="updateSetting($event, 'title')"
                        :value="title"
                        :placeholder="$t('setting_placeholder_title')"
                        :disabled="!canEditQuiz"
                        id="quiz-title"
                        type="text"
                        class="mt-1"
                        name="title"
                        data-vv-as="quiz/poll title"
                    />
                </div>
                <span
                    v-if="errors.has('title') && !title"
                    class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
                >
                    <i class="fas fa-exclamation-circle"></i> {{ errors.first('title') }}
                </span>
                <div class="mt-4 shadow-text-none">
                    <label id="number-of-question-id" for="number-of-question" class="font-bold">{{
                        $t('setting_number_question')
                    }}</label>
                    <XInput
                        v-validate="'required|min_value:1'"
                        @input="updateSetting($event, 'show_number_question')"
                        @blur="checkedToValidate()"
                        :value="showNumberQuestion"
                        :placeholder="$t('setting_placeholder_show_number_question')"
                        :disabled="!canEditQuiz"
                        id="number-of-question"
                        type="text"
                        class="mt-1"
                        pattern="[0-9]+"
                        min="0"
                        maxlength="4"
                        name="number of question"
                    />
                </div>
                <span
                    v-if="errors.has('number of question')"
                    class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
                >
                    <i class="fas fa-exclamation-circle"></i> {{ errors.first('number of question') }}
                </span>
                <div class="flex items-center mt-4">
                    <XCheckbox
                        @input="updateRandomQuestion($event)"
                        :square="true"
                        :checked="randomQuestion"
                        :disabled="!canEditQuiz"
                        id="random-question"
                        type="checkbox"
                        size="lg"
                        theme="dark"
                    >
                        <label id="random-question-id" for="random-question" class="font-bold shadow-text-none">
                            {{ $t('setting_header_random_question') }}
                        </label>
                    </XCheckbox>
                </div>
                <template v-if="isTeacherPlus">
                    <XDivider class="my-6" color="#ccc" />
                    <div class="share-box">
                        <p class="font-bold text-center shadow-text-none">
                            {{ $t('setting_header_share_with_teacher') }}
                        </p>
                        <p class="font-bold text-center shadow-text-none">
                            {{ $t('setting_description_share_with_teacher') }}
                        </p>
                        <div class="flex justify-center md:items-center flex-col md:flex-row mt-4">
                            <div class="flex flex-col">
                                <XCheckbox
                                    @input="updateShowSchoolList($event)"
                                    :square="true"
                                    :checked="showSchoolList"
                                    :disabled="status === false"
                                    id="show-school-list"
                                    type="checkbox"
                                    size="lg"
                                    theme="dark"
                                >
                                    <label for="show-school-list" class="font-bold shadow-text-none">
                                        {{ $t('setting_show_school_list') }}
                                    </label>
                                </XCheckbox>
                            </div>
                            <div class="flex flex-col">
                                <XCheckbox
                                    @input="updateShowSchoolCountryList($event)"
                                    :square="true"
                                    :checked="showSchoolCountryList"
                                    :disabled="status === false"
                                    id="show-school-country-list"
                                    type="checkbox"
                                    size="lg"
                                    theme="dark"
                                >
                                    <label for="show-school-country-list" class="font-bold shadow-text-none">
                                        {{ $t('setting_show_school_country_list') }}
                                    </label>
                                </XCheckbox>
                            </div>
                        </div>
                        <div class="text-center mt-2">
                            <i v-if="!status" class="text-red text-sm mt-2">
                                ({{ $t('setting_inactive_quiz_can_not_set_to_school') }})
                            </i>
                        </div>
                    </div>
                    <div class="subject-box flex flex-col md:flex-row mt-6">
                        <div class="w-full shadow-text-none">
                            <span class="font-bold">{{ $t('setting_class_subject') }}</span>
                            <XSubjectSelect
                                @input="updateClassSubject($event)"
                                :options="subjectLists.map(subject => ({ name: subject.name, label: subject.label }))"
                                :value="classSubject"
                                :placeholder="$t('setting_header_class_subject')"
                                :disabled="status === false"
                                label="name"
                                class="mt-1 input input-block input-text-center quiz-dropdown"
                                name="updateSubject"
                                dense
                                outlined
                            ></XSubjectSelect>
                        </div>
                        <div class="w-full shadow-text-none">
                            <span class="font-bold">{{ $t('setting_class_grade') }}</span>
                            <XSelect
                                @input="updateClassGrade($event)"
                                :options="gradeList.map(grade => ({ value: grade.value, label: grade.label }))"
                                :value="classGrade"
                                :placeholder="$t('setting_header_class_grade')"
                                :disabled="status === false"
                                label="label"
                                class="mt-1 input input-block input-text-center quiz-dropdown"
                                name="updateClassGrade"
                                multiple
                            ></XSelect>
                        </div>
                    </div>
                </template>
                <XDivider class="mt-10 mb-4" color="#ccc" />
                <div class="flex justify-center items-center">
                    <span class="shadow-text-none cursor-pointer" @click="onShowOption">
                        {{ showOptionSettings ? '-' : '+' }} {{ $t('setting_optional_settings') }}
                    </span>
                </div>
                <div v-if="showOptionSettings">
                    <div class="mt-4 shadow-text-none">
                        <div class="flex items-center justify-between">
                            <label id="description-id" for="description" class="font-bold">{{
                                $t('setting_placeholder_description')
                            }}</label>

                            <XButton
                                v-if="allowRobotQuiz"
                                v-tippy="{
                                    placement: 'top-center',
                                    arrow: true,
                                    arrowType: 'round',
                                    animation: 'fade'
                                }"
                                @click.prevent="generateDescriptionByAi"
                                :disabled="
                                    isGeneratingAiDescription ||
                                    !title ||
                                    (title && title.toLowerCase() === 'untitled') ||
                                    !canEditQuiz
                                "
                                :content="$t('setting_generate_description_by_ai')"
                                class="system-button-color system-button-text-color shadow-text-none"
                            >
                                <i
                                    :class="[
                                        'fas text-sm',
                                        isGeneratingAiDescription ? 'fa-sync-alt fa-spin' : 'fa-robot'
                                    ]"
                                ></i>
                            </XButton>
                        </div>
                        <XTextarea
                            @input="updateDescription"
                            @blur="checkedToValidate()"
                            :value="description"
                            :resizeable="true"
                            :disabled="!canEditQuiz"
                            id="description"
                            type="text"
                            class="mt-1"
                            maxlength="200"
                            rows="1"
                        />
                    </div>
                    <div class="mt-4 shadow-text-none">
                        <label id="custom-message-id" for="custom-message" class="font-bold">{{
                            $t('setting_placeholder_custom_message')
                        }}</label>
                        <XTextarea
                            @input="updateSetting($event, 'custom_message')"
                            :value="customMessage"
                            :resizeable="true"
                            :disabled="!canEditQuiz"
                            id="custom-message"
                            type="text"
                            class="mt-1"
                            maxlength="60"
                            rows="1"
                        />
                    </div>
                    <div v-if="!schoolId">
                        <div class="mt-10 shadow-text-none">
                            <p class="font-size-16 font-bold text-center shadow-text-none">
                                {{ $t('setting_age_recommended') }}
                            </p>
                            <SliderBar
                                @change="val => updateQuizAge(val)"
                                :class="{ 'mt-8': true, 'pointer-events-none': !canEditQuiz }"
                                :value="age"
                                :min="0"
                                :max="99"
                                :dotSize="30"
                                color="#000"
                            ></SliderBar>
                        </div>
                        <div class="mt-4 shadow-text-none">
                            <p class="font-size-16 font-bold text-center shadow-text-none">
                                {{ $t('setting_difficult_level') }}
                            </p>
                            <SliderBar
                                @change="updateQuizDifficult($event)"
                                :class="{ 'mt-8': true, 'pointer-events-none': !canEditQuiz }"
                                :value="difficult"
                                :min="0"
                                :max="10"
                                :dotSize="30"
                                :rangeText="[
                                    $t('setting_slider_easy'),
                                    $t('setting_slider_normal'),
                                    $t('setting_slider_hard')
                                ]"
                                color="#000"
                            ></SliderBar>
                        </div>
                    </div>
                </div>
                <template v-if="owner && id">
                    <XDivider class="my-4" color="#ccc" />
                    <div v-if="id" class="flex items-center">
                        <div
                            @click="$emit('delete-quiz', id)"
                            class="delete-btn flex items-center shadow-text-none cursor-pointer mr-4"
                        >
                            <i class="far fa-trash-alt trash-icon"></i>
                            <span>{{ $t('setting_button_delete_quiz') }}</span>
                        </div>
                        <XToggleSwitch
                            @input="toggleQuizStatus($event)"
                            :checked="status"
                            :border-checked-active="true"
                            :content="$t('question_active')"
                            :disabled="!canEditQuiz"
                            class="shadow-text-none"
                            height="24"
                            color="#5a5351"
                            border-weight="3"
                            border-color="#5a5351"
                            checked-color="#28a745"
                        />
                    </div>
                </template>
            </div>
        </div>
        <UpgradeAiQuestionModal
            v-if="isShowUpgradeAiQuestionModal"
            @success="onSuccess"
            @close="onClose"
            ref="upgradeAiQuestion"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { currentUser, getToken } from '@/lib/user.js'
import appConfig from '@/app.config.js'
import Request from '@/lib/request.js'
import XInput from '@/components/v2/XInput'
import XTextarea from '@/components/v2/XTextarea'
import XCheckbox from '@/components/v2/XCheckbox'
import XDivider from '@/components/v2/XDivider'
import XSelect from '@/components/v2/XSelect'
import XButton from '@/components/v2/XButton'
import XSubjectSelect from '@/components/v2/XSubjectSelect.vue'
import datalist from '@/mixins/datalist'
import SliderBar from '@/components/SliderBar.vue'
import popup from '@/lib/popup'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import UserService from '@/lib/user.js'
import InnerLoader from '@/components/InnerLoader'
import UpgradeAiQuestionModal from '@/components/UpgradeAiQuestionModal'
import { load } from 'recaptcha-v3'
import QuizCreatorImage from '@/components/quiz-creator/QuizCreatorImage'
import quizCreator from '@/mixins/quiz-creator'
import XToggleSwitch from '@/components/v2/XToggleSwitch'

export default {
    components: {
        XInput,
        XTextarea,
        XCheckbox,
        XDivider,
        XSelect,
        XSubjectSelect,
        XButton,
        SliderBar,
        InnerLoader,
        UpgradeAiQuestionModal,
        QuizCreatorImage,
        XToggleSwitch
    },

    mixins: [datalist, introJsData, quizCreator],

    data() {
        return {
            showOptionSettings: false,
            showModal: true,
            introHint: new IntroHint(),
            isGeneratingAiDescription: false,
            isLoading: false,
            limitAi: 0,
            isShowUpgradeAiQuestionModal: false
        }
    },

    created() {
        this.isLoading = true

        this.introHint.removeHints()
        this.getLimitAiQuestionByUser()

        setTimeout(() => {
            this.introHint.register(this.hintsQuizSettings)
        }, 500)

        if (this.isTeacher) {
            this.getSchoolSubject(currentUser().id)
        }
    },

    mounted() {
        if (this.isTeacherRoles) {
            this.onShowOption()
        }
    },

    computed: {
        ...mapState({
            id: state => state.question._id,
            title: state => state.question.title,
            type: state => state.question.type,
            status: state => state.question.status,
            showNumberQuestion: state => state.question.show_number_question,
            randomQuestion: state => state.question.is_random_question_order,
            showSchoolList: state => state.question.show_school_list,
            showSchoolCountryList: state => state.question.show_school_country_list,
            classSubject: state => state.question.class_subject,
            subjectLists: state => state.subjectList,
            classGrade: state => state.question.class_grade,
            description: state => state.question.description,
            customMessage: state => state.question.custom_message,
            age: state => state.question.age,
            difficult: state => state.question.difficult,
            isCreateNewQuiz: state => state.isCreateNewQuiz,
            image: state => state.question.image,
            imageOriginal: state => state.question.image_original,
            imageSource: state => state.question.image_source,
            imageCoordinates: state => state.question.image_coordinates,
            questions: state => state.question.questions,
            minimumQuestionToShare: state => state.minimumQuestionToShare,
            schoolId: state => state.question.school_id
        }),

        //level of teacher
        isTeacher() {
            return currentUser().role == 'teacher' || currentUser().role == 'teacher_plus'
        },

        allowRobotQuiz() {
            return !currentUser().disable_quiz_ai ? true : false
        }
    },

    methods: {
        async closeModal() {
            this.introHint.removeHints()
            this.introHint.register(this.hintsQuestionList)

            const validate = await this.$validator.validate()

            if (!validate) {
                if (this.errors.has('title')) {
                    popup.alert(this.$t('setting_alert_title_required'))
                }

                if (this.errors.has('number of question')) {
                    popup.alert(this.$t('setting_alert_number_question_required'))
                }

                return
            }

            this.showModal = false

            setTimeout(() => {
                this.$emit('close')
            }, 300)
        },

        updateSetting(event, field) {
            if (!this.canEditQuiz) return

            if (field == 'show_number_question') {
                event.target.value = event.target.value.replace(/^0+|[^0-9]/g, '')
            }

            const value = event.target.value

            this.$store.commit('updateSetting', { value, field })
        },

        updateRandomQuestion: function (event) {
            if (!this.canEditQuiz) return

            const value = event.target.checked
            this.$store.commit('updateSetRandomQuestion', value)
        },

        getSchoolSubject(id) {
            this.$store.dispatch('getSchoolSubjectById', id)
        },

        updateShowSchoolList(event) {
            const validate = this.validateToShare()

            if (!validate) {
                event.target.checked = false
            }

            this.$store.commit('updateShowSchoolList', event.target.checked)

            if (event.target.checked) {
                this.showOptionSettings = true
            }
        },

        updateShowSchoolCountryList(event) {
            const validate = this.validateToShare()

            if (!validate) {
                event.target.checked = false
            }

            this.$store.commit('updateShowSchoolCountryList', event.target.checked)

            if (event.target.checked) {
                this.showOptionSettings = true
            }
        },

        updateClassSubject(subject) {
            this.$store.commit('updateClassSubject', subject)
            this.checkedToValidate()
        },

        updateClassGrade(grade) {
            this.$store.commit('updateClassGrade', grade)
            this.checkedToValidate()
        },

        updateDescription(event) {
            if (!this.canEditQuiz) return

            let value = event.target.value

            const regex = /(&nbsp;|<([^>]+)>)/gi
            value = value.replace(regex, '')

            this.$store.commit('updateDescription', value)
        },

        updateQuizAge(age) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateQuizAge', age)
        },

        updateQuizDifficult(difficult) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateQuizDifficult', difficult)
        },

        onShowOption() {
            this.showOptionSettings = !this.showOptionSettings

            this.introHint.removeHints()
            setTimeout(() => {
                this.introHint.register(this.hintsQuizSettings)
            }, 500)
        },

        async generateDescriptionByAi() {
            await this.getLimitAiQuestionByUser()

            if (1 > this.limitAi) {
                this.introHint.removeHints()

                popup.confirm(
                    this.$t('setting_modal_alert_limit_description'),
                    () => {
                        this.isShowUpgradeAiQuestionModal = true
                    },
                    () => {
                        this.introHint.register(this.hintsQuizSettings)
                    },
                    {
                        customButtons: {
                            ok: {
                                text: this.$t('setting_modal_button_limit_description'),
                                className: 'btn font-opensans text-md system-button-color system-button-text-color'
                            }
                        }
                    }
                )
                return
            }

            this.isGeneratingAiDescription = true
            const req = new Request({
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            })
            const request = this.createRequest()

            const recaptcha = await load(this.getRecaptchaSiteKey(), {
                useRecaptchaNet: true,
                autoHideBadge: true
            })

            const token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })

            await req
                .post(appConfig.api.generateDescriptionByAi, {
                    captcha_token: token,
                    title: this.title
                })
                .then(async res => {
                    await request.put(appConfig.api.setLimitAiQuestionByUser, { user_id: currentUser().id })

                    this.isGeneratingAiDescription = false
                    this.updateDescription({ target: { value: res.data.data.description } })
                })
                .catch(() => {
                    this.isGeneratingAiDescription = false
                })
        },

        getRecaptchaSiteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        },

        createRequest() {
            const userService = new UserService()
            const token = userService.getToken()

            return new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
        },

        async getLimitAiQuestionByUser() {
            const request = this.createRequest()

            await request.post(appConfig.api.getLimitAiQuestionByUser).then(res => {
                this.limitAi = res.data.data.limit
                this.isLoading = false
            })
        },

        onSuccess() {
            this.isLoading = true
            this.getLimitAiQuestionByUser()
        },

        onClose() {
            this.isShowUpgradeAiQuestionModal = false
            this.introHint.register(this.hintsQuizSettings)
        },

        checkedToValidate() {
            const payload = {
                target: {
                    checked: true
                }
            }

            if (this.showSchoolList) {
                this.updateShowSchoolList(payload)
            }

            if (this.showSchoolCountryList) {
                this.updateShowSchoolCountryList(payload)
            }
        },

        async toggleQuizStatus() {
            try {
                this.$store.commit('toggleMainLoader', true)

                const toggle = await this.$store.dispatch('toggleQuizStatus', this.id)

                if (toggle.status !== 200) throw toggle

                this.$store.commit('setQuizStatus', toggle.data.data.current_status)
                this.$store.commit('toggleMainLoader', false)
            } catch {
                this.$store.commit('toggleMainLoader', false)
            }
        }
    }
}
</script>

<style lang="scss">
#quiz-config-setting-modal {
    &.show {
        background-color: rgba(0, 0, 0, 0.5);
        .modal-content {
            opacity: 1;
            transform: translateY(0px);
        }
    }

    .modal-content {
        .modal-header {
            border-bottom: 1px solid #ccc;
        }

        .modal-body {
            .share-box {
                > div {
                    gap: 1rem 2rem;
                }
            }

            .subject-box {
                gap: 0.5rem;
            }

            .delete-btn {
                column-gap: 0.5rem;
                color: #ef0000;
            }
        }
    }
}
</style>
